import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useEffect, useMemo, useState } from "react";
import { MessageSquare, FileText, CalendarCheck2, Wrench, GraduationCap } from "lucide-react";
import FreeAuditForm from "./FreeAuditForm";

interface ServiceItem {
  id: string;
  title: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  benefit: string;
  bullets: string[]; // 2 bullets
  details: string[]; // full explanation paragraphs
}

const services: ServiceItem[] = [
  {
    id: "ai-chat-assistants",
    title: "AI Chat Assistants",
    Icon: MessageSquare,
    benefit: "24/7 answers, booking, and lead qualification",
    bullets: [
      "Cuts 2–6 hours of admin weekly",
      "Ideal for law, dental, gyms",
    ],
    details: [
      "We build branded assistants that greet visitors on your website and social channels, answer common questions, qualify leads, and book appointments hands‑free.",
      "Assistants are trained on your policies, pricing, and tone. Handoffs to staff are seamless when needed, and transcripts are logged for transparency.",
      "Expect faster response times, less inbox triage, and a better client experience—especially after hours.",
    ],
  },
  {
    id: "document-text-automation",
    title: "Document/Text Automation",
    Icon: FileText,
    benefit: "Summarize, draft, and route notes automatically",
    bullets: [
      "Learns your templates and tone",
      "Fewer manual errors, faster work",
    ],
    details: [
      "We streamline note‑taking, intake summaries, drafts, and document generation.",
      "Your approved samples become the source of truth, so outputs stay consistent with your brand and compliance needs.",
      "Great for case updates, visit summaries, and templated comms that take too long today.",
    ],
  },
  {
    id: "smart-scheduling-workflows",
    title: "Smart Scheduling & Workflows",
    Icon: CalendarCheck2,
    benefit: "Reduce no‑shows with synced reminders & follow‑ups",
    bullets: [
      "Syncs with calendars/CRM",
      "Low‑lift onboarding",
    ],
    details: [
      "Connect your Google/Outlook calendars and existing tools to automate reminders, confirmations, and rescheduling flows.",
      "We'll map your current process, then add the right nudges so clients show up and staff waste less time.",
    ],
  },
  {
    id: "custom-ai-solutions",
    title: "Custom AI Solutions",
    Icon: Wrench,
    benefit: "Tailored automations to your stack and industry",
    bullets: [
      "Automate repetitive tasks",
      "Improve productivity and ROI",
    ],
    details: [
      "When you have a unique workflow, we build to fit—integrations, internal tools, and specialty automations that move the needle.",
      "We start small, measure impact, and scale what works across your team.",
    ],
  },
  {
    id: "workshops-training",
    title: "Workshops & Training",
    Icon: GraduationCap,
    benefit: "Hands-on learning for teams across AI tools and digital skills",
    bullets: [
      "AI literacy training and prompt engineering",
      "Custom tool workshops for your team",
    ],
    details: [
      "We deliver hands-on workshops that empower your team to confidently use AI tools and modern digital workflows in their daily work.",
      "Our training covers AI literacy fundamentals, effective prompt engineering techniques, and practical sessions with the specific tools that matter most to your industry—whether that's ChatGPT, document automation, scheduling tools, or custom solutions.",
      "Each workshop is tailored to your team's current skill level and business goals, ensuring everyone walks away with actionable knowledge they can apply immediately to save time and improve outcomes.",
    ],
  },
];

const Services = () => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Store the last selected service but don't auto-open on refresh
  useEffect(() => {
    const last = sessionStorage.getItem("lastService");
    if (last && services.some((s) => s.id === last)) {
      setSelectedId(last);
      // Removed auto-opening behavior to prevent popup after refresh
    }
  }, []);

  useEffect(() => {
    if (selectedId) sessionStorage.setItem("lastService", selectedId);
  }, [selectedId]);

  const selected = useMemo(
    () => services.find((s) => s.id === selectedId) || null,
    [selectedId]
  );

  return (
    <section id="services" className="py-16 md:py-24">
      <div className="container mx-auto">
        <header className="mb-10 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold">Services</h2>
          <p className="text-lg md:text-xl text-muted-foreground mt-3">
            Compact overview. Click "Details" to see the full breakdown and book your free consultation.
          </p>
        </header>

        <div className="flex flex-wrap gap-6 md:gap-8 justify-center max-w-6xl mx-auto">
          {services.map(({ id, title, Icon, benefit, bullets }) => (
            <Card key={id} className="h-full min-h-[320px] w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] flex flex-col">
              <CardHeader className="flex-row items-center gap-3">
                <span className="inline-flex size-10 items-center justify-center rounded-md bg-secondary">
                  <Icon className="text-primary" aria-hidden />
                </span>
                <CardTitle className="text-xl">{title}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                <div>
                  <p className="text-base text-foreground font-medium">{benefit}</p>
                  <ul className="list-disc pl-5 space-y-1.5 mt-3 text-base text-muted-foreground">
                    {bullets.slice(0, 2).map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
                <div className="mt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-haspopup="dialog"
                    aria-controls={`service-${id}-dialog`}
                    onClick={() => {
                      setSelectedId(id);
                      setOpen(true);
                    }}
                  >
                    Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(newOpen) => {
        setOpen(newOpen);
        // Reset form state when dialog closes to prevent validation issues
        if (!newOpen) {
          // Allow a small delay to let any pending validation settle
          setTimeout(() => {
            // The form will reset naturally when the dialog reopens
          }, 100);
        }
      }}>
        <DialogContent id={`service-${selected?.id}-dialog`} className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.title}</DialogTitle>
                <DialogDescription>
                  {selected.benefit}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-base text-muted-foreground">
                {selected.details.map((d, i) => (
                  <p key={i}>{d}</p>
                ))}
              </div>
              <div className="mt-4">
                <h3 className="text-base font-semibold mb-2">Book Free Consultation</h3>
                {/* Inline form - key prop ensures form resets when dialog reopens */}
                <FreeAuditForm key={open ? 'open' : 'closed'} onSubmitted={() => setOpen(false)} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Services;
